import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import ConfigDrivenContainer from '../components/ConfigDriven/ConfigDrivenContainer.tsx';
import { useApplication } from '../hooks/useApplication.ts';
import { useOnboardingTracking } from '../hooks/useOnboardingTracking.ts';
import { BorrowerDto } from '../openapi/atlantis/index.ts';
import {
  ApplicationControllerApi,
  ContentItem,
  OnboardingResponseRoleEnum,
} from '../openapi/yenta/index.ts';
import ErrorService from '../services/ErrorService.ts';
import {
  fetchAgentOnboardingApplicationProgress,
  saveCurrentQuestion,
} from '../slices/ApplicationSlice.ts';
import { fetchDropboxById } from '../slices/DropboxSlice.ts';
import { useAppDispatch, useAppSelector } from '../slices/store.ts';
import Logger from '../utils/Logger.ts';
import {
  getLegacyApplicationId,
  isCreateAccountSectionIntro,
} from '../utils/OnboardingHelpers.ts';
import { getYentaConfiguration } from '../utils/OpenapiConfigurationUtils.tsx';

/**
 * Agent Onboarding Route
 * This route is for agents with CANDIDATE status to complete their onboarding process
 */
const AgentOnboardingRoute: React.FC = () => {
  const {
    auth: { authUserDetail },
    application: { currentQuestion },
  } = useAppSelector((state) => state);
  const dispatch = useAppDispatch();
  const {
    trackApplicationStarted,
    trackSelfServeOnboardingApplicationStarted,
  } = useOnboardingTracking();
  const legacyApplicationId = getLegacyApplicationId(
    authUserDetail,
    OnboardingResponseRoleEnum.Agent,
  );
  const abortControllerRef = useRef<AbortController | null>(null);

  // Initialize AbortController and cleanup on unmount to cancel in-flight API requests
  useEffect(() => {
    abortControllerRef.current = new AbortController();

    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  // Fetch application data to get team invitation info
  const { data: applicationData } = useApplication(legacyApplicationId);

  // Extract team invitation properties for self-serve onboarding tracking
  const teamInviteProps = useMemo(() => {
    if (!applicationData?.teamInvitation) return null;

    const invitationId =
      applicationData.teamInvitation.genericTeamInvitation ||
      applicationData.teamInvitation.invitationId;

    const teamId = applicationData.teamInvitation.teamId;

    if (!invitationId || !teamId) return null;

    return {
      team_invite_id: invitationId,
      team_id: teamId,
      invite_method: applicationData.teamInvitation.genericTeamInvitation
        ? ('link' as const)
        : ('email' as const),
    };
  }, [applicationData]);

  const fetchDropboxData = useCallback(async () => {
    if (authUserDetail?.id && authUserDetail?.dropboxId) {
      dispatch(
        fetchDropboxById(
          {
            id: authUserDetail.id,
            dropboxId: authUserDetail.dropboxId,
          } as BorrowerDto,
          {
            signal: abortControllerRef.current?.signal,
          },
        ),
      );
    }
  }, [authUserDetail?.dropboxId, authUserDetail?.id, dispatch]);

  useEffect(() => {
    fetchDropboxData();
  }, [fetchDropboxData]);

  // Track application started only when user lands on "Create Account" section intro
  useEffect(() => {
    if (isCreateAccountSectionIntro(legacyApplicationId, currentQuestion)) {
      trackApplicationStarted('agent_onboarding_screen');
    }
  }, [legacyApplicationId, currentQuestion, trackApplicationStarted]);

  const onStart = async (_stepId: string) => {
    try {
      await new ApplicationControllerApi(
        await getYentaConfiguration(),
      ).acknowledgeSection(legacyApplicationId!, currentQuestion?.id!);

      // Track self-serve onboarding application started for team-invited users
      if (
        isCreateAccountSectionIntro(legacyApplicationId, currentQuestion) &&
        teamInviteProps
      ) {
        try {
          trackSelfServeOnboardingApplicationStarted(
            'agent_onboarding_screen',
            teamInviteProps,
            'team member',
            "Let's Go",
          );
        } catch (error) {
          Logger.error(
            'Failed to track self-serve onboarding application started event',
            error,
          );
        }
      }
    } catch (e: any) {
      Logger.error('Failed to acknowledge section:', e);
      ErrorService.notify('Failed to acknowledge section', e);
    }
  };

  const onNext = async (): Promise<ContentItem> => {
    try {
      const { data } = await new ApplicationControllerApi(
        await getYentaConfiguration(),
      ).getNextStep(legacyApplicationId!, undefined, {
        signal: abortControllerRef.current?.signal,
      });

      await dispatch(saveCurrentQuestion(data));
      await dispatch(
        fetchAgentOnboardingApplicationProgress(legacyApplicationId!, {
          signal: abortControllerRef.current?.signal,
        }),
      );

      return data;
    } catch (e: any) {
      Logger.error('Failed to fetch next question:', e);
      ErrorService.notify('Failed to fetch next question', e);
      throw e;
    }
  };

  const onNavigateToQuestion = async (
    questionId: string,
  ): Promise<ContentItem> => {
    try {
      const { data } = await new ApplicationControllerApi(
        await getYentaConfiguration(),
      ).getAnsweredStep(legacyApplicationId!, questionId);

      await dispatch(saveCurrentQuestion(data));
      await dispatch(
        fetchAgentOnboardingApplicationProgress(legacyApplicationId!),
      );

      return data;
    } catch (e: any) {
      Logger.error('Failed to fetch question by id:', e);
      ErrorService.notify('Failed to fetch question by id', e);
      throw e;
    }
  };

  const onSubmit = async (formData: any) => {
    try {
      await new ApplicationControllerApi(
        await getYentaConfiguration(),
      ).answerQuestion(
        legacyApplicationId!,
        currentQuestion?.id!,
        JSON.stringify({ answers: formData.answers }),
      );
    } catch (e: any) {
      Logger.error('Failed to submit question answer:', e);
      ErrorService.notify('Failed to submit question answer', e);
    }
  };

  if (!legacyApplicationId) {
    return (
      <div className='flex min-h-screen flex-col items-center justify-center bg-gray-50 px-4 py-12 sm:px-6 lg:px-8'>
        <div className='w-full max-w-md space-y-8'>
          <div className='mt-8 bg-white px-6 py-8 shadow sm:rounded-lg sm:px-10'>
            <p className='text-center text-gray-500'>
              No application found. Please contact support.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ConfigDrivenContainer
      onStart={onStart}
      onNext={onNext}
      onSubmit={onSubmit}
      onNavigateToQuestion={onNavigateToQuestion}
    />
  );
};

export default AgentOnboardingRoute;
